import { IErrors, IlogUpdate, Tabelas } from "../interfaces";

export const SINTER_STATUS_COLUNA = "status";
export const SINTER_TIPO_DESATIVACAO_COLUNA = "tipo_desativacao_sinter";
export const SINTER_MOTIVO_DESATIVACAO_COLUNA = "motivo_desativacao_sinter";
export const SINTER_TABELA_IMOBILIARIO: Tabelas = "imobiliario";

export const SINTER_ALERTA_INATIVACAO =
  "Para inativar o imobiliário, é obrigatório preencher Tipo desativação da UI e Motivo da desativação da UI.";

export const SINTER_CONFIRMACAO_INATIVACAO =
  "Você está inativando este imobiliário. Essa ação também irá inativar o cadastro no SINTER. Deseja continuar?";

export const SINTER_ERRO_OBRIGATORIO = "Campo obrigatório para inativação no SINTER";
export const SINTER_ERRO_MOTIVO_INCOMPATIVEL = "Motivo incompatível com o tipo de desativação selecionado";

const TIPO_EXTINCAO = "1";
const TIPO_NULIDADE = "2";

const MOTIVOS_EXTINCAO = ["01", "02", "03", "04", "05", "06", "07"];
const MOTIVOS_NULIDADE = ["06", "07", "08", "09", "10"];

type BuildSinterErrorsParams = {
  id?: string | number;
  status: unknown;
  tipo: unknown;
  motivo: unknown;
};

function normalize(value: unknown): string {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
}

function normalizeCode(value: unknown): string {
  const normalized = normalize(value);
  if (!normalized) return "";

  const onlyDigits = normalized.match(/\d+/)?.[0];
  if (onlyDigits) {
    return onlyDigits.padStart(2, "0");
  }

  return normalized;
}

function isEmptySinterValue(value: unknown): boolean {
  const normalized = normalize(value);
  return normalized === "" || normalized === "NULL" || normalized === "UNDEFINED" || normalized === "-1";
}

export function isStatusAtivo(value: unknown): boolean {
  const normalized = normalize(value);
  return (
    normalized === "1" ||
    normalized === "ATIVO" ||
    (normalized.includes("ATIVO") && !normalized.includes("INATIVO") && !normalized.includes("DESATIVADO"))
  );
}

export function isStatusInativo(value: unknown): boolean {
  const normalized = normalize(value);
  return (
    normalized === "2" ||
    normalized === "INATIVO" ||
    normalized === "DESATIVADO" ||
    normalized.includes("INATIVO") ||
    normalized.includes("DESATIVADO")
  );
}

export function isTipoExtincao(value: unknown): boolean {
  const normalized = normalize(value);
  return normalized === TIPO_EXTINCAO || normalized.includes("EXTINCAO");
}

export function isTipoNulidade(value: unknown): boolean {
  const normalized = normalize(value);
  return normalized === TIPO_NULIDADE || normalized.includes("NULIDADE");
}

export function isCampoDesativacaoSinter(tblNomeColuna?: string): boolean {
  return tblNomeColuna === SINTER_TIPO_DESATIVACAO_COLUNA || tblNomeColuna === SINTER_MOTIVO_DESATIVACAO_COLUNA;
}

export function getMotivosPermitidosPorTipo(tipo: unknown): string[] {
  if (isTipoExtincao(tipo)) return MOTIVOS_EXTINCAO;
  if (isTipoNulidade(tipo)) return MOTIVOS_NULIDADE;
  return [];
}

export function isMotivoCompativelComTipo(motivo: unknown, tipo: unknown): boolean {
  if (isEmptySinterValue(motivo)) return true;

  const motivosPermitidos = getMotivosPermitidosPorTipo(tipo);
  if (motivosPermitidos.length === 0) return true;

  return motivosPermitidos.includes(normalizeCode(motivo));
}

export function optionMatchesTipoDesativacao(option: { id?: unknown; nome?: unknown; raw?: any }, tipo: unknown): boolean {
  const motivosPermitidos = getMotivosPermitidosPorTipo(tipo);
  if (motivosPermitidos.length === 0) return true;

  const optionCode = normalizeCode(option.id);
  if (optionCode && !motivosPermitidos.includes(optionCode)) return false;

  const tipoAceito = normalize(option.raw?.tipo_aceito ?? option.raw?.tipoAceito ?? option.raw?.tipo_desativacao_aceita);
  const nome = normalize(option.nome);

  if (isTipoExtincao(tipo)) {
    return tipoAceito.includes("EXTINCAO") || nome.includes("EXTINCAO");
  }

  if (isTipoNulidade(tipo)) {
    return tipoAceito.includes("NULIDADE") || nome.includes("NULIDADE");
  }

  return true;
}

export function buildSinterDesativacaoErrors(params: BuildSinterErrorsParams): IErrors {
  const { id, status, tipo, motivo } = params;
  const errors: IErrors = {};

  if (!id || !isStatusInativo(status)) return errors;

  const base = {
    tabela: SINTER_TABELA_IMOBILIARIO,
    id: String(id),
    severity: "error",
  };

  if (isEmptySinterValue(tipo)) {
    errors[`${id}@${SINTER_TABELA_IMOBILIARIO}@${SINTER_TIPO_DESATIVACAO_COLUNA}`] = {
      ...base,
      coluna: SINTER_TIPO_DESATIVACAO_COLUNA,
      message: SINTER_ERRO_OBRIGATORIO,
    };
  }

  if (isEmptySinterValue(motivo)) {
    errors[`${id}@${SINTER_TABELA_IMOBILIARIO}@${SINTER_MOTIVO_DESATIVACAO_COLUNA}`] = {
      ...base,
      coluna: SINTER_MOTIVO_DESATIVACAO_COLUNA,
      message: SINTER_ERRO_OBRIGATORIO,
    };
  } else if (!isMotivoCompativelComTipo(motivo, tipo)) {
    errors[`${id}@${SINTER_TABELA_IMOBILIARIO}@${SINTER_MOTIVO_DESATIVACAO_COLUNA}`] = {
      ...base,
      coluna: SINTER_MOTIVO_DESATIVACAO_COLUNA,
      message: SINTER_ERRO_MOTIVO_INCOMPATIVEL,
    };
  }

  return errors;
}

export function hasStatusInativoUpdate(log?: { updates?: IlogUpdate[] } | null): boolean {
  return !!log?.updates?.some(
    (update) =>
      update.nome_tabela === SINTER_TABELA_IMOBILIARIO &&
      update.nome_campo === SINTER_STATUS_COLUNA &&
      isStatusInativo(update.valor_campo),
  );
}
